import { autoUpdate, FloatingFocusManager, FloatingOverlay, FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react'
import { css } from '@linaria/core'
import { type FC, useEffect } from 'react'
import { ToastElement } from '#components/Toast'
import { useToast } from './useToast'

const toastOverlayClass = css`
  @media print {
    display: none;
  }
  z-index: 1000;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const toastQueueClass = css`
  position: fixed;

  display: flex;
  flex-direction: column;
  gap: 12px;

  isolation: isolate;

  &[data-placement="top"] {
    left: 50%;
    top: 12px;
    transform: translate(-50%, 0);
  }

  &[data-placement="top-left"] {
    top: 12px;
    left: 12px;
  }

  &[data-placement="top-right"] {
    top: 12px;
    right: 12px;
  }

  &[data-placement="bottom"] {
    left: 50%;
    bottom: 12px;
    transform: translate(-50%, 0);
  }

  &[data-placement="bottom-left"] {
    bottom: 12px;
    left: 12px;
  }

  &[data-placement="bottom-right"] {
    bottom: 12px;
    right: 12px;
  }
`

export const QueuedToasts: FC<{
  placement: 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right'
}> = ({ placement }) => {
  const { toasts, remove } = useToast()

  useEffect(() => {
    let handle: number | undefined

    const tick = () => {
      const anyDismissals = toasts.filter((x) => x.visibleUntil)
      if (anyDismissals.length <= 0) {
        return
      }

      const earliestRemoval = anyDismissals.reduce((a, b) => ((a.visibleUntil ?? 0) < (b.visibleUntil ?? 0) ? a : b))
      handle = setTimeout(
        () => {
          remove(earliestRemoval.id)
          tick()
        },
        (earliestRemoval.visibleUntil ?? 0) - Date.now(),
      ) as unknown as number
    }

    tick()
    return () => {
      clearTimeout(handle)
    }
  }, [toasts, remove])

  const { refs, context } = useFloating({
    open: toasts.length > 0,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })

  return (
    <FloatingPortal>
      <FloatingOverlay unselectable="on" className={toastOverlayClass}>
        <FloatingFocusManager context={context}>
          <div ref={refs.setFloating} className={toastQueueClass} data-placement={placement}>
            {toasts.map((x) => {
              // either a dismiss is given or visibleUntil has to be given
              // if neither is true, add a dismiss element to not clog the queue
              const needsDismiss = !!x.onDismiss || !x.visibleUntil

              return (
                <ToastElement
                  key={x.id}
                  {...x}
                  onDismiss={
                    needsDismiss
                      ? () => {
                          x.onDismiss?.()
                          remove(x.id)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  )
}
